<!-- src/pages/CarDetail.vue -->
<template>
    <div class="car-detail-layout">
        <AppHeader />
        <div v-if="loading" class="status-message">
            <div class="spinner"></div>
            <p>{{ $t('loading_car_details') }}</p>
        </div>
        <div v-else-if="error" class="status-message error">
            <i class="error-icon">⚠️</i>
            <p>{{ error }}</p>
            <button @click="router.go(-1)" class="back-button">{{ $t('back_to_listings') }}</button>
        </div>
        <div v-else-if="car" class="container">
            <div class="car-header">
                <div class="title-wrapper">
                    <h1>{{ car.make }} {{ car.model }}
                        <span class="year">({{ car.year }})</span>
                    </h1>
                    <!-- <div class="price">{{ $n(car.price, 'currency') }}</div> -->
                </div>
                <div class="meta-info">
                    <span class="meta-item">{{ $n(car.mileage, 'number') }} {{ $t('kilometers') }}</span>
                    <span class="meta-item">{{ $t(`fuel_types.${car.fuel_type.toLowerCase()}`) }}</span>
                    <span class="meta-item">{{ $t(`transmissions.${car.transmission.toLowerCase()}`) }}</span>
                </div>
            </div>
            
            <div class="main-content-row">
                <div class="car-gallery">
                    <div class="main-image-container">
                        <div class="main-image" :style="{ backgroundImage: `url(${currentImage})` }"></div>
                    </div>
                    <div class="thumbnail-gallery">
                        <div 
                            v-for="(image, index) in car.images" 
                            :key="index" 
                            class="thumbnail" 
                            :class="{ active: currentImage === image }" 
                            :style="{ backgroundImage: `url(${image})` }" 
                            @click="setCurrentImage(image)"
                        ></div>
                    </div>
                </div>
                
                <div class="specs-box">
                    <div class="specs-list">
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.make') }}:</span>
                            <span class="spec-value">{{ car.make }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.model') }}:</span>
                            <span class="spec-value">{{ car.model }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.year') }}:</span>
                            <span class="spec-value">{{ car.year }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.price') }}:</span>
                            <span class="spec-value">{{ $n(car.price, 'currency') }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.mileage') }}:</span>
                            <span class="spec-value">{{ $n(car.mileage, 'number') }} {{ $t('kilometers') }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.fuel_type') }}:</span>
                            <span class="spec-value">{{ $t(`fuel_types.${car.fuel_type.toLowerCase()}`) }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.transmission') }}:</span>
                            <span class="spec-value">{{ $t(`transmissions.${car.transmission.toLowerCase()}`) }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.power') }}:</span>
                            <span class="spec-value">{{ car.power_kw }} {{ $t(`car_power.kilowatts`) }} / {{ calculateHorsepower(car.power_kw) }} {{ $t(`car_power.horse_power`) }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.doors') }}:</span>
                            <span class="spec-value">{{ car.doors }}</span>
                        </div>
                        <div class="spec-item">
                            <span class="spec-label">{{ $t('specs.color') }}:</span>
                            <span class="spec-value">{{ $t(`colors.${car.color.toLowerCase()}`) }}</span>
                        </div>
                        <div class="price">{{ $n(car.price, 'currency') }}</div>
                        <!-- <a v-if="car.carpass_url" v-bind:href="car.carpass_url" style="text-align: right; margin-top: 55px; cursor: pointer;" target="_blank"><img :src="check_car_pass" style="width: 150px"></a> -->
                        <div v-if="car.carpass_url" class="carpass-link">
                            <a :href="car.carpass_url" target="_blank">
                                <img :src="check_car_pass" alt="Check Car Pass" />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div class="contact-section">
                <div class="contact-card">
                    <div style="text-align: left">
                        <h3>{{ $t('interested_title') }}</h3>
                        <p>{{ $t('interested_text') }}</p>
                    </div>
                    <button @click="goToContact" class="contact-button">
                        <i class="icon">✉️</i> {{ $t('contact_seller') }}
                    </button>
                </div>
            </div>

            <div class="equipment-section" v-if="car.equipment && car.equipment.length">
                <h3 class="equipment-title">{{ $t('equipment') }}</h3>
                
                <div class="equipment-categories">
                    <div v-for="(category, index) in groupedEquipment" :key="index" class="equipment-category">
                        <h4 class="category-title">{{ category.name }}</h4>
                        <ul class="equipment-list">
                            <li v-for="(item, itemIndex) in category.items" :key="itemIndex" class="equipment-item">
                                {{ item.id.formatted }}
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
            
            <div class="description-section">
                <h3 class="description-title">{{ $t('description') }}</h3>
                <div class="description-content" v-html="$t('description_car_detail')"></div>
            </div>

            <div class="contact-section">
                <div class="contact-card" style="flex-direction: column;">
                    <div>
                        <h3>{{ $t('interested_title') }}</h3>
                        <p>{{ $t('interested_text') }}</p>
                    </div>
                    <button @click="goToContact" class="contact-button" style="margin-top: 20px;">
                        <i class="icon">✉️</i> {{ $t('contact_seller') }}
                    </button>
                </div>
            </div>
        </div>
        <AppFooter />
    </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useCarsStore } from '@/stores/cars'
import { useI18n } from 'vue-i18n'
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'

const check_car_pass = ref(require('@/assets/images/Check-Car-Pass-Logo.png'))

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const carsStore = useCarsStore()

const car = ref(null)
const loading = ref(false)
const error = ref(null)
// const showContactForm = ref(false)
const currentImage = ref('')

const setCurrentImage = (image) => {
    currentImage.value = image
}

const groupedEquipment = computed(() => {
    if (!car.value?.equipment) return []
    
    const categories = {}
    
    car.value.equipment.forEach(item => {
        const categoryName = item.category.formatted || 'Other'
        if (!categories[categoryName]) {
            categories[categoryName] = []
        }
        categories[categoryName].push(item)
    })
    
    return Object.entries(categories).map(([name, items]) => ({
        name,
        items
    }))
})

const goToContact = () => {
    router.push({
        name: 'contact',
        query: {
            carImage: car.value.images[0],
            carMake: car.value.make,
            carModel: car.value.model,
            carYear: car.value.year
        }
    })
}

onMounted(async () => {
    try {
        window.scrollTo({ top: 0, behavior: 'smooth' })
        loading.value = true
        car.value = await carsStore.fetchCarById(route.params.id)
        if (car.value?.images?.length) {
            currentImage.value = car.value.images[0]
        }
    } catch (err) {
        error.value = err.message || t('load_error')
    } finally {
        loading.value = false
    }
})

const calculateHorsepower = (kw) => {
    return Math.trunc(parseInt(kw) * 1.34102); 
}
</script>

<style scoped>
.car-detail-layout {
    background: white;
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    margin-top: 4.8rem;
}

.status-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
    text-align: center;
    gap: 15px;
}

.car-detail {
    max-width: 1200px;
    margin: 0 auto;
    padding: 20px;
    color: #333;
}

.error-icon {
    font-size: 2.5rem;
}

.spinner {
    border: 5px solid #f3f3f3;
    border-top: 5px solid #3498db;
    border-radius: 50%;
    width: 50px;
    height: 50px;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.back-button {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 8px 15px;
    background: #f0f0f0;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    font-size: 0.9rem;
    transition: all 0.2s;
    margin: 20px;
}

.back-button:hover {
    background: #e0e0e0;
}

.arrow {
    font-weight: bold;
}

.car-header {
    margin: 30px 0;
}

.title-wrapper {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
}

h1 {
    font-size: 2rem;
    margin: 0;
    color: #222;
}

.year {
    color: #666;
    font-weight: normal;
}

.price {
    font-size: 2.8rem;
    color: #0066cc;
    font-weight: bold;
    margin-top: 25px;
    text-align: center;
}

.meta-info {
    display: flex;
    gap: 15px;
    color: #666;
}

.meta-item {
    display: flex;
    align-items: center;
}

.meta-item:not(:last-child):after {
    content: "•";
    margin-left: 15px;
    color: #0066cc;
}

.main-content-row {
    display: flex;
    gap: 30px;
    margin-bottom: 30px;
}

.car-gallery {
    width: 65%;
}

.main-image-container {
    width: 100%;
    aspect-ratio: 16/13;
    margin-bottom: 15px;
    border-radius: 8px;
    overflow: hidden;
/*    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);*/
}

.main-image {
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    transition: transform 0.3s ease;
}

.main-image:hover {
    transform: scale(1.02);
}

.thumbnail-gallery {
    display: flex;
    gap: 10px;
    overflow-x: auto;
    padding-bottom: 10px;
}

.thumbnail {
    width: 80px;
    height: 60px;
    min-width: 80px;
    background-size: cover;
    background-position: center;
    border-radius: 4px;
    cursor: pointer;
    transition: all 0.2s ease;
}

.thumbnail:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
}

.thumbnail.active {
    border-color: #0066cc;
    transform: scale(1.05);
}

.specs-box {
    flex: 1;
    background: #f9f9f9;
    border-radius: 8px;
    padding: 20px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.specs-list {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
}

.spec-item {
    display: flex;
    justify-content: space-between;
    padding: 8px 0;
    border-bottom: 1px solid #eee;
}

.spec-label {
    font-weight: 600;
    color: #555;
}

.spec-value {
    color: #222;
    text-align: right;
}

.description-section {
    margin-bottom: 40px;
}

.description-title {
    font-size: 1.5rem;
    margin-bottom: 15px;
    color: #222;
    border-bottom: 2px solid #45ABFF;
    padding-bottom: 8px;
}

.description-content {
    line-height: 1.6;
    color: #444;
}

.description-content :deep(p) {
    margin-bottom: 15px;
}

.description-content :deep(ul),
.description-content :deep(ol) {
    padding-left: 20px;
    margin-bottom: 15px;
}

.description-content :deep(li) {
    margin-bottom: 5px;
}

.contact-section {
/*    padding: 0 20px;*/
    margin-bottom: 40px;
}

.contact-card {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f0f7ff;
    border-radius: 8px;
    padding: 25px;
    text-align: center;
}

.contact-card h3 {
    margin-top: 0;
    font-size: 1.4rem;
    color: #0066cc;
}

.contact-card p {
    color: #555;
    margin-bottom: 0px;
}

.contact-button {
    background: #0066cc;
    color: white;
    border: none;
    padding: 12px 25px;
    border-radius: 4px;
    font-size: 1rem;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    transition: background 0.2s;
}

.contact-button:hover {
    background: #0055aa;
}

.icon {
    font-size: 1.1rem;
}
.carpass-link {
    display: flex;
    justify-content: center;
    margin-top: 20px;
}

.carpass-link img {
    width: 150px;
    cursor: pointer;
    transition: transform 0.3s ease;
}


.carpass-link img:hover {
    transform: scale(1.05);
}
.thumbnail-gallery::-webkit-scrollbar {
    height: 6px;
}

.thumbnail-gallery::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

.thumbnail-gallery::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 3px;
}

.thumbnail-gallery::-webkit-scrollbar-thumb:hover {
    background: #555;
}

.equipment-section {
    margin-bottom: 40px;
}

.equipment-title {
    font-size: 1.5rem;
    margin-bottom: 20px;
    color: #222;
    border-bottom: 2px solid #45ABFF;
    padding-bottom: 8px;
}

.equipment-categories {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 20px;
}

.equipment-category {
    background: #f9f9f9;
    border-radius: 8px;
    padding: 15px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.category-title {
    font-size: 1.1rem;
    margin-top: 0;
    margin-bottom: 10px;
    color: #0066cc;
}

.equipment-list {
    list-style-type: none;
    padding-left: 0;
    margin: 0;
}

.equipment-item {
    padding: 5px 0;
    border-bottom: 1px solid #eee;
    font-size: 0.9rem;
}

.equipment-item:last-child {
    border-bottom: none;
}

@media (max-width: 768px) {
    .equipment-categories {
        grid-template-columns: 1fr;
    }
}

@media (max-width: 768px) {
    .title-wrapper h1 {
        font-size: 1.5rem;
    }

    .title-wrapper .price {
        font-size: 1.5rem;
    }

    .main-content-row {
        flex-direction: column;
    }
    
    .car-gallery {
        flex: 1 1 100%;
    }
    
    .specs-box {
        margin-top: 20px;
    }

    .meta-info {
        gap: 5px;
    }

    .meta-item:not(:last-child):after {
        margin-left: 5px;
        font-size: 14px;
    }

    .car-gallery {
        width: 100%;
    }

    .car-detail-layout {
        margin-top: 4.5rem;
    }
}
</style>